/**
 * Soft-safe RolePolicy adapters — Layer 2 Kit vertical slice.
 * Healer: Soft-HP in-staff triage (no Claim B OOR chase).
 * DPS: PackChew / Elite / break-window focus — tags only, in-band later.
 *
 * Extension: registerHeroKit / registerRolePolicy / registerHeroAdapter
 * (hero-registry). Match roleTags — never defId===.
 */
import { RaidEnemyTag, enemyHasTag, enemyIsCombatRelevant } from "../raid-enemy";
import type { RaidBoss, RaidUnit } from "../raid-types";
import {
  bossBreakEncounterKnowledge,
  bossBreakWindowOpen,
  unitBreakWindowOpen,
} from "./encounter-break";
import { packChargeOverlapKnowledge } from "./encounter-pack-charge";
import type {
  HeroJudgmentAdapter,
  KitActionProposal,
  RolePolicy,
} from "./hero-architecture";
import {
  ensureDefaultHeroKits,
  registerEncounterKnowledge,
  registerHeroAdapter,
  rolePolicyTable,
} from "./hero-registry";
import { judgmentNorm } from "./traits";

function livingAllies(allies: readonly RaidUnit[], selfId: number): RaidUnit[] {
  return allies.filter((a) => a.hp > 0 && a.id !== selfId);
}

function dist(
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  return Math.hypot(ax - bx, ay - by);
}

function needsHeal(a: RaidUnit, belowPct: number): boolean {
  if (a.hp <= 0) return false;
  if (belowPct >= 1) return a.hp < a.maxHp;
  return a.hp / a.maxHp < belowPct;
}

/** Soft-HP most-hurt in-staff — Claim B banned (OOR never). */
export const healerKitPolicy: RolePolicy = {
  id: "healer_kit_soft_triage",
  roleTags: ["healer", "support"],
  proposeAction(args): KitActionProposal | null {
    if (!args.kit.roleTags.includes("healer")) return null;
    if (!args.kit.actions.includes("heal")) return null;
    const insightT = judgmentNorm(args.traits.insight);
    const minInsight = rolePolicyTable().healerTriageInsightNormMin;
    // Insight↓: simple/fast — skip kit triage (polar reserved; Soft keeps fallback).
    if (insightT < minInsight) return null;
    const belowPct = args.belowPct ?? 1;
    const reach = args.hero.attackRange;
    let best: RaidUnit | null = null;
    let bestRatio = 1;
    for (const a of livingAllies(args.allies, args.hero.id)) {
      if (!needsHeal(a, belowPct)) continue;
      const d = dist(args.hero.x, args.hero.y, a.x, a.y);
      if (d >= reach) continue;
      const r = a.hp / a.maxHp;
      if (
        r < bestRatio ||
        (r === bestRatio && (best == null || a.id < best.id))
      ) {
        bestRatio = r;
        best = a;
      }
    }
    if (!best) return null;
    return {
      actionId: "heal",
      subjectId: best.id,
      urgency01: Math.max(0, Math.min(1, 1 - bestRatio)),
      reasonTag: "soft_hp_in_staff",
    };
  },
};

/**
 * Focus: break-window melt > PackChew on feet > Elite in view.
 * Caller must Soft≪AA-gate to in-band only (kit-policy-apply).
 */
export const dpsFocusPolicy: RolePolicy = {
  id: "dps_focus_pack_or_elite",
  roleTags: ["dps", "focus_dps", "tank"],
  proposeAction(args): KitActionProposal | null {
    const roles = args.kit.roleTags;
    if (
      !roles.includes("dps") &&
      !roles.includes("focus_dps") &&
      !roles.includes("tank")
    ) {
      return null;
    }
    const insightT = judgmentNorm(args.traits.insight);
    if (insightT < rolePolicyTable().dpsFocusInsightNormMin) return null;

    const boss = args.boss ?? null;
    if (boss && bossBreakWindowOpen(boss)) {
      return {
        actionId: "attack",
        subjectId: -1, // RAID_AGGRO_BOSS_ID
        urgency01: 0.9,
        reasonTag: "break_window_melt",
      };
    }

    let bestBroken: RaidUnit | null = null;
    let bestChew: RaidUnit | null = null;
    let bestElite: RaidUnit | null = null;
    let bestChewD = Infinity;
    let bestEliteD = Infinity;
    let bestBrokenD = Infinity;

    for (const e of args.enemies) {
      if (!enemyIsCombatRelevant(e)) continue;
      const d = dist(args.hero.x, args.hero.y, e.x, e.y);
      if (unitBreakWindowOpen(e) && d < bestBrokenD) {
        bestBrokenD = d;
        bestBroken = e;
      }
      if (enemyHasTag(e, RaidEnemyTag.PackChew) && d < bestChewD) {
        bestChewD = d;
        bestChew = e;
      }
      if (enemyHasTag(e, RaidEnemyTag.Elite) && d < bestEliteD) {
        bestEliteD = d;
        bestElite = e;
      }
    }

    const pick = bestBroken ?? bestChew ?? bestElite;
    if (!pick) return null;
    const reasonTag = bestBroken
      ? "elite_break_focus"
      : bestChew
        ? "pack_chew_focus"
        : "elite_focus";
    return {
      actionId: args.kit.actions.includes("focus") ? "focus" : "attack",
      subjectId: pick.id,
      urgency01: Math.max(0.25, Math.min(1, 0.45 + 0.35 * Math.max(0, insightT))),
      reasonTag,
    };
  },
};

export const healerKitAdapter: HeroJudgmentAdapter = {
  id: "adapter_healer_kit",
  matches(_hero, kit) {
    return (
      kit.roleTags.includes("healer") || kit.roleTags.includes("support")
    );
  },
  role: healerKitPolicy,
  polarAxes: ["insight"],
};

export const dpsFocusAdapter: HeroJudgmentAdapter = {
  id: "adapter_dps_focus",
  matches(_hero, kit) {
    return (
      kit.roleTags.includes("dps") ||
      kit.roleTags.includes("focus_dps") ||
      kit.roleTags.includes("tank")
    );
  },
  role: dpsFocusPolicy,
  polarAxes: ["insight", "bond"],
};

export const HERO_JUDGMENT_STUB_ADAPTERS: readonly HeroJudgmentAdapter[] = [
  healerKitAdapter,
  dpsFocusAdapter,
];

/** Seed registry once (idempotent). Import side-effect via judgment barrel. */
export function ensureHeroJudgmentDefaults(): void {
  ensureDefaultHeroKits();
  registerHeroAdapter(healerKitAdapter);
  registerHeroAdapter(dpsFocusAdapter);
  registerEncounterKnowledge(bossBreakEncounterKnowledge);
  registerEncounterKnowledge(packChargeOverlapKnowledge);
}

ensureHeroJudgmentDefaults();

/** @internal test helper */
export function _bossForPolicy(boss: RaidBoss | null | undefined): RaidBoss | null {
  return boss ?? null;
}
